import { useCallback, useRef, useState } from 'react';

export interface OverlayState<T> {
  /** What the overlay is about. Outlives `open` until the exit has finished. */
  value: T | null;
  open: boolean;
  /**
   * Changes on every `show`. Use it in the overlay's `key` — as
   * `` key={`room-${state.session}`} ``, with a prefix of its own, because two
   * sibling overlays both keyed `1` are duplicate keys to React. A reopening
   * that lands during the previous exit then mounts a fresh dialog, instead
   * of handing the new subject a dialog still holding the last one's typed
   * fields (room B opened with room A's name in the box, ready to save).
   */
  session: number;
  show: (next: T) => void;
  hide: () => void;
  /** Hand to `Modal.afterClose`, or call from `Drawer.afterOpenChange(false)`. */
  afterClose: () => void;
}

/**
 * The subject of a modal or drawer, kept until its exit animation has run.
 *
 * `{editing ? <Modal open … /> : null}` and `open={id !== null}` both drop the
 * subject the instant the user closes: the first unmounts the dialog so it
 * vanishes with no exit at all, the second keeps it mounted but re-renders the
 * closing frames with an empty title — «ارسال کمپین «»» sliding away. Here
 * `open` flips at once and `value` is cleared only when AntD reports the exit
 * finished, so the dialog leaves looking exactly as it did.
 */
export function useOverlayState<T>(): OverlayState<T> {
  const [value, setValue] = useState<T | null>(null);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(0);
  // Read by `afterClose`: an exit that finishes after the overlay was opened
  // again must not clear the subject it is now showing.
  const openRef = useRef(false);

  const show = useCallback((next: T) => {
    openRef.current = true;
    setSession((current) => current + 1);
    setValue(next);
    setOpen(true);
  }, []);
  const hide = useCallback(() => {
    openRef.current = false;
    setOpen(false);
  }, []);
  const afterClose = useCallback(() => {
    if (!openRef.current) setValue(null);
  }, []);

  return { value, open, session, show, hide, afterClose };
}
